import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Suspense } from "react";
import { getGoldRates } from "@/lib/gold-prices.functions";
import { computeCityPrices, currencyFor, formatMoney, type Country } from "@/lib/gold-data";

const ratesQO = queryOptions({
  queryKey: ["gold-rates"],
  queryFn: () => getGoldRates(),
  staleTime: 10 * 60 * 1000,
});

export const Route = createFileRoute("/embed/gold/$country")({
  parseParams: ({ country }) => {
    if (country !== "pakistan" && country !== "india") throw notFound();
    return { country: country as Country };
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(ratesQO),
  head: ({ params }) => ({
    meta: [
      { title: `Live ${params.country === "pakistan" ? "Pakistan" : "India"} Gold Rate Widget` },
      { name: "robots", content: "noindex,follow" },
      { httpEquiv: "Content-Security-Policy", content: "frame-ancestors *;" },
    ],
  }),
  component: EmbedWidget,
  notFoundComponent: () => <div style={{ padding: 16 }}>Not found</div>,
  errorComponent: () => <div style={{ padding: 16, color: "#888" }}>Widget unavailable</div>,
});

function EmbedWidget() {
  const { country } = Route.useParams();
  return (
    <Suspense fallback={<Skeleton />}>
      <EmbedInner country={country} />
    </Suspense>
  );
}

function Skeleton() {
  return (
    <div style={{ padding: 12, fontFamily: "system-ui, sans-serif", color: "#888" }}>Loading live rate…</div>
  );
}

function EmbedInner({ country }: { country: Country }) {
  const { data } = useSuspenseQuery(ratesQO);
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const p = computeCityPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: fx, country, cityPremium: 1 });
  const currency = currencyFor(country);
  const mainPrice = country === "pakistan" ? p.perTola24 : p.per10g24;
  const altPrice = country === "pakistan" ? p.perTola22 : p.per10g22;
  const unit = country === "pakistan" ? "tola" : "10g";
  const flag = country === "pakistan" ? "🇵🇰" : "🇮🇳";
  return (
    <div
      style={{
        margin: 0,
        background: "linear-gradient(135deg,#0f0f1a 0%,#1a1330 100%)",
        color: "#f5e9c8",
        fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,sans-serif",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxSizing: "border-box",
        padding: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1 }}>
          {flag} Live {country === "pakistan" ? "Pakistan" : "India"} Gold Rate
        </div>
        <div style={{ width: 7, height: 7, borderRadius: 99, background: "#ef4444", boxShadow: "0 0 8px #ef4444" }} />
      </div>
      <div style={{ fontSize: 13, opacity: 0.65 }}>24K per {unit}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "#D4AF37", lineHeight: 1.1 }}>
        {formatMoney(mainPrice, currency)}
      </div>
      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
        22K: <b>{formatMoney(altPrice, currency)}</b>
      </div>
      <a
        href={`https://drhint.com/${country}`}
        target="_blank"
        rel="noopener"
        style={{
          marginTop: 8,
          fontSize: 10,
          color: "#9aa0b4",
          textDecoration: "none",
          alignSelf: "flex-end",
        }}
      >
        Powered by drhint.com ↗
      </a>
    </div>
  );
}